import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { samplePages } from '../data/content';
import { CtaButton } from './CtaButton';
export function LookInside() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(samplePages.length - 1, index));
    const slide = track.children[clamped] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let minDistance = Infinity;
        Array.from(track.children).forEach((child, index) => {
          const el = child as HTMLElement;
          const distance = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
          if (distance < minDistance) {
            minDistance = distance;
            closest = index;
          }
        });
        setActive(closest);
      });
    };
    track.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(frame);
    };
  }, []);
  const current = samplePages[active];
  return <section aria-roledescription="carousel" aria-label="Sample pages from the Hospitality English Ready toolkit" className="w-full overflow-hidden bg-stone-900 py-20 text-white sm:py-28" data-id="element-128" data-mp="3sgnmhm05kh29">
      <div className="mx-auto max-w-3xl px-5 text-center" data-id="element-129" data-mp="2zs9kyw9uv2ly">
        <h2 className="text-[2rem] font-black leading-[1.08] tracking-tight sm:text-5xl" data-id="element-130" data-mp="2zrq65ofi4uc5">
          Take a Look <span className="italic text-red-500" data-id="element-131" data-mp="13mr3jm6xa0b8">Inside.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/60 sm:text-base" data-id="element-132" data-mp="18mn75kbpjpq9">
          Real pages from the toolkit — worksheets, visual tasks, role-play
          cards, and teacher keys, ready to print and teach tomorrow morning.
        </p>

        <div role="tablist" aria-label="Choose a sample page" className="mt-8 inline-flex rounded-full bg-white/10 p-1" data-id="element-133" data-mp="kqius8epb7b6">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <button key={page.level} type="button" role="tab" aria-selected={isActive} onClick={() => scrollToIndex(index)} className={`whitespace-nowrap rounded-full px-2.5 py-2 text-xs font-extrabold tracking-wide transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 sm:px-5 sm:text-sm ${isActive ? 'bg-red-500 text-white' : 'text-white/60 hover:text-white'}`} data-id="element-134" data-mp="tt0eog6vvnv0" data-mpi={index}>
                {page.level}
              </button>;
        })}
        </div>
      </div>

      <div className="relative mt-12" data-id="element-135" data-mp="26fmrsj9131he">
        <div ref={trackRef} className="flex [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x snap-mandatory gap-5 overflow-x-auto px-[8vw] pb-2 sm:gap-8 sm:px-[22vw] lg:px-[31vw]" data-id="element-136" data-mp="21je94vvmgq5h">
          {samplePages.map((page, index) => {
          const isActive = index === active;
          return <figure key={page.level} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${samplePages.length}: ${page.title}`} className="w-[84vw] shrink-0 snap-center sm:w-[56vw] lg:w-[38vw]" data-id="element-137" data-mp="1nkoc1g8syqh4" data-mpi={index}>
                <button type="button" onClick={() => scrollToIndex(index)} tabIndex={isActive ? -1 : 0} aria-label={`Show ${page.level} sample page`} className={`block w-full overflow-hidden rounded-2xl bg-white p-2 shadow-xl transition-[opacity,transform] duration-300 ease-[cubic-bezier(0.23,1,0.32,1)] focus:outline-none focus-visible:ring-4 focus-visible:ring-red-500/60 ${isActive ? 'scale-100 opacity-100' : 'scale-[0.92] opacity-40'}`} data-id="element-138" data-mp="3oxzwmqjqxgcu">
                  <img src={page.image} alt={`${page.level} sample page: ${page.title}`} className="aspect-[4/5] w-full rounded-xl object-cover object-center" loading="lazy" draggable={false} data-id="element-139" data-mp="2w92gasz3htmq" />
                </button>
              </figure>;
        })}
        </div>

        <button type="button" onClick={() => scrollToIndex(active - 1)} disabled={active === 0} aria-label="Previous page" className="absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-600 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:left-4 sm:h-12 sm:w-12 lg:left-[calc(31vw-4.5rem)]" data-id="element-140" data-mp="28puptkg6a2me">
          <ChevronLeftIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-141" data-mp="31e8nfz3cmwxf" />
        </button>
        <button type="button" onClick={() => scrollToIndex(active + 1)} disabled={active === samplePages.length - 1} aria-label="Next page" className="absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-red-500 text-white shadow-xl transition-[opacity,transform,background-color] duration-150 hover:scale-105 hover:bg-red-600 active:scale-95 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40 disabled:pointer-events-none disabled:opacity-30 sm:right-4 sm:h-12 sm:w-12 lg:right-[calc(31vw-4.5rem)]" data-id="element-142" data-mp="2wfp2o20hrr6n">
          <ChevronRightIcon className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" data-id="element-143" data-mp="2lrm7knkgq2ik" />
        </button>
      </div>

      <div className="mx-auto mt-8 max-w-md px-5 text-center" aria-live="polite" data-id="element-144" data-mp="2qpe6w2nfsqws">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.16em] text-red-500" data-id="element-145" data-mp="2voqwgv5p0c32">
          {current.caption}
        </p>
        <h3 className="mt-2 text-xl font-bold sm:text-2xl" data-id="element-146" data-mp="2zpkljruvfwxj">
          {current.title}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-white/60" data-id="element-147" data-mp="11ckijolezh72">
          {current.text}
        </p>

        <div className="mt-6 flex items-center justify-center gap-2 sm:hidden" data-id="element-148" data-mp="15dcu4ohrdy6i">
          {samplePages.map((page, index) => <span key={page.level} aria-hidden="true" className={`h-1.5 rounded-full transition-[width,background-color] duration-200 ${index === active ? 'w-6 bg-red-500' : 'w-1.5 bg-white/30'}`} data-id="element-149" data-mp="xv6av0yr3xwb" data-mpi={index} />)}
        </div>

        <p className="mt-8 text-xs font-semibold text-white/50" data-id="element-150" data-mp="1gs7g8mw7w00s">
          +60 lessons like these across 6 volumes
        </p>
        <CtaButton href="#offer" className="mt-4" data-id="element-151" data-mp="1bsuvc7owp30u" />
      </div>
    </section>;
}